import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  Save,
  Clock3
} from "lucide-react";
import * as ownerApi from "../../api/payroll/shiftRuleApi";
import * as portalApi from "../../api/payroll/shiftRulePortalApi";
import {
  Button,
  Card,
  CardContent,
  Input,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function ShiftRuleForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/shift-rules" : "/shift-rules";
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    name: "",
    code: "",
    startTime: "09:00",
    endTime: "17:00",
    gracePeriodMinutes: 15,
    shiftAllowance: 0,
    isDefault: false,
    active: true,
  });
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.getAllShiftRules();
        const list = Array.isArray(res) ? res : res?.data || [];
        const found = list.find((x) => x._id === id);
        if (found) {
          setForm({
            name: found.name || "",
            code: found.code || "",
            startTime: found.startTime || "09:00",
            endTime: found.endTime || "17:00",
            gracePeriodMinutes: found.gracePeriodMinutes ?? 15,
            shiftAllowance: found.shiftAllowance ?? 0,
            isDefault: !!found.isDefault,
            active: found.active !== false,
          });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Load failed");
      }
    })();
  }, [id, isPortal, api]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        shiftAllowance: Number(form.shiftAllowance),
        gracePeriodMinutes: Number(form.gracePeriodMinutes),
      };
      if (id) await api.updateShiftRule(id, payload);
      else await api.createShiftRule(payload);
      navigate(base);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <Clock3 className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          {id ? "Edit" : "Create"} Shift Rule
        </h1>
      </div>

      

      <Card>
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />

              <Input
                label="Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Start"
                type="time"
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                required
              />

              <Input
                label="End"
                type="time"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Grace (min)"
                type="number"
                value={form.gracePeriodMinutes}
                onChange={(e) => setForm({ ...form, gracePeriodMinutes: e.target.value })}
              />

              <Input
                label="Shift allowance"
                type="number"
                value={form.shiftAllowance}
                onChange={(e) => setForm({ ...form, shiftAllowance: e.target.value })}
              />
            </div>

            <div className="space-y-3">
              <Checkbox
                checked={form.isDefault}
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                label="Default shift"
              />
              <Checkbox
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                label="Active"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" variant="primary" isLoading={loading} leftIcon={<Save className="h-4 w-4" />}>
                Save
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate(base)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}